import { ElementType, PropsWithChildren } from "react";

import { twMerge } from "tailwind-merge";

import { ClassName, PolymorphicProps } from "shared/types";

import { Spinner } from "../spinner";

type Theme = "dodgerBlue" | "white";

type Props = {
  disabled?: boolean;
  isLoading?: boolean;
  theme?: Theme;
} & ClassName;

export const Button = <E extends ElementType = "button">(
  props: PropsWithChildren<PolymorphicProps<E, Props>>
) => {
  const {
    as: Comp = "button",
    children,
    className,
    disabled = false,
    isLoading = false,
    theme = "dodgerBlue",
    ...restProps
  } = props;

  const content = isLoading ? (
    <span className="opacity-0">{children}</span>
  ) : (
    children
  );

  return (
    <Comp
      {...restProps}
      className={twMerge(
        "relative inline-flex cursor-pointer justify-center rounded-lg px-[18px] py-2 text-center font-medium shadow-xs transition-colors select-none",
        (isLoading || disabled) && "pointer-events-none",
        theme === "dodgerBlue" &&
          "bg-dodgerBlue hover:bg-dodgerBlueDark focus:bg-dodgerBlueDark active:bg-dodgerBlueLight text-white",
        theme === "dodgerBlue" && disabled && "bg-dodgerBlue/50",

        theme === "white" &&
          "text-fiord hover:bg-athensGray focus:bg-athensGray bg-white active:brightness-90",
        theme === "white" && disabled && "text-gullGray",
        className
      )}
    >
      {isLoading && (
        <span className="absolute inset-0 flex items-center justify-center">
          <Spinner
            className={twMerge(
              "size-4",
              theme === "dodgerBlue" && "text-dodgerBlue/60 stroke-white/60"
            )}
          />
        </span>
      )}
      {content}
    </Comp>
  );
};
